/**
 * Single source of truth for the composer predicates that both the inbox (`MessageList`)
 * and the lead-management messaging window (`RenterMessagingModal`) need, and that `AddMessage`
 * applies internally. They live here rather than being re-derived per call site because each
 * copy is a place the surfaces can silently diverge — the attach button and the drag-to-attach
 * drop zone disagreeing, say, or one parent gating replies differently from the other.
 */

import StringConstants from '../constants/StringConstants';

type CanAttachArgs = {
  canSendMessage: boolean;
  isPhoneLead: boolean;
  isSpam: boolean;
  isConversationBlockedByZillow: boolean;
};

/**
 * Whether attachments can be added to a conversation. Deliberately excludes impersonation:
 * an impersonator can't type or send, but the attach button has never been gated on it.
 */
export const canAttachToConversation = ({
  canSendMessage,
  isPhoneLead,
  isSpam,
  isConversationBlockedByZillow,
}: CanAttachArgs): boolean => canSendMessage && !isPhoneLead && !isSpam && !isConversationBlockedByZillow;

type ComposerDisabledArgs = {
  /** `canSendMessage` from `useLeadRestrictions` — already accounts for the rollout flag. */
  canSendMessage: boolean;
  /** The backend's copy for the SEND_MESSAGE restriction, when one is present. */
  sendDisabledMessage: string | undefined;
  isImpersonator: boolean;
  /**
   * Supplied by the parent: `getMessages` doesn't carry it, so `MessageList` and the
   * lead-management window each pass the value they already hold for the composer.
   */
  isPhoneLead: boolean;
  isSpam: boolean;
  isConversationBlockedByZillow: boolean;
};

/**
 * Why replying is withheld, or `undefined` when it is allowed. Four surfaces share this ladder —
 * the composer tooltip, the saved-reply chip beside it, the failed-bubble retry (which posts
 * `addMessage` directly), and the Contacts `SendMessageModal` — so two of them can never explain
 * the same conversation differently. The Contacts endpoint carries neither `disabledFeatures` nor
 * spam status, so that surface can only answer the impersonation, phone-lead and Zillow-blocked
 * rungs and passes the other two as unrestricted. Each reason carries its own copy, with the
 * restriction first because the backend's already reflects its highest-priority reason, so a
 * viewer-state reason like impersonation never reads as a lead restriction.
 *
 * Callers feeding `TooltipWrapper` must coerce with `?? ''`: its `tooltipText` default is the
 * impersonation copy, so a bare `undefined` would surface the wrong reason entirely.
 */
export const getComposerDisabledText = ({
  canSendMessage,
  sendDisabledMessage,
  isImpersonator,
  isPhoneLead,
  isSpam,
  isConversationBlockedByZillow,
}: ComposerDisabledArgs): string | undefined => {
  if (!canSendMessage) {
    return sendDisabledMessage || StringConstants.TOOLTIP.MESSAGING_RESTRICTED;
  }
  if (isPhoneLead) {
    return StringConstants.TOOLTIP.PHONE_LEAD;
  }
  if (isImpersonator) {
    return StringConstants.TOOLTIP.IMPERSONATION;
  }
  if (isConversationBlockedByZillow) {
    return StringConstants.TOOLTIP.BLOCKED_BY_ZILLOW;
  }
  if (isSpam) {
    return StringConstants.TOOLTIP.REPORTED;
  }
  return undefined;
};

/**
 * Whether replying is withheld at all. Derived from the ladder above so a rung added there gates
 * the controls too, instead of leaving them enabled with no tooltip.
 */
export const isComposerDisabled = (args: ComposerDisabledArgs): boolean => getComposerDisabledText(args) !== undefined;

type CanSendArgs = {
  /** `canSendMessage` from `useLeadRestrictions` — already accounts for the rollout flag. */
  isSendAllowedByRestrictions: boolean;
  isRestrictionRolloutEnabled: boolean;
  /** The conversation query settled in an error state. */
  hasConversationError: boolean;
  /** The conversation query has data, whether or not a later refetch failed. */
  hasConversationData: boolean;
};

/**
 * Whether replying is allowed, combining the lead's restrictions with the one case the
 * restrictions can't speak to: a conversation that never loaded, whose restrictions are
 * therefore unknowable. Polling stops on a 4xx, so that state is permanent and a reply we
 * can't vet must not be offered.
 *
 * Data presence — not query health — is the test: a failed background refetch also sets the
 * query's error flag, but restrictions already loaded stay valid, so replying must survive it.
 * Scoped to the rollout so none of this is reachable before StreetEasy restrictions ship.
 */
export const canSendToConversation = ({
  isSendAllowedByRestrictions,
  isRestrictionRolloutEnabled,
  hasConversationError,
  hasConversationData,
}: CanSendArgs): boolean => {
  const hasUnknowableRestrictions = hasConversationError && !hasConversationData;
  return isSendAllowedByRestrictions && !(isRestrictionRolloutEnabled && hasUnknowableRestrictions);
};
